import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import { useNavigate } from "react-router-dom";

const SLIDES = [
  {
    id: 1,
    image: "/assets/images/main-1.jpg",
    title: "Where Adventure\nMeets Authenticity",
    sub: "Witness the world's highest peaks through the eyes of those who call them home.",
  },
  {
    id: 2,
    image: "/assets/images/carousel.jpeg",
    title: "Sacred Traditions\nTowering Peaks",
    sub: "Prayer flags flutter above ancient monasteries and glacial trails carved by centuries.",
  },
  {
    id: 3,
    image: "/assets/images/Fewa.jpeg",
    title: "Guided by Generations\nof Sherpa Wisdom",
    sub: "Every step guided by families who have walked these mountains for generations.",
  },
];

const STATS = [
  { icon: "Mountain", value: "15+", label: "Trek Routes" },
  { icon: "Users", value: "5+", label: "Expert Guides" },
  { icon: "Star", value: "98%", label: "Success Rate" },
  { icon: "Globe", value: "100+", label: "Happy Trekkers" },
];

const HeroSection = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [transitioning, setTransitioning] = useState(false);

  useEffect(() => {
    const t = setInterval(() => {
      setTransitioning(true);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % SLIDES.length);
        setTransitioning(false);
      }, 500);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const goTo = (i) => {
    if (i === current) return;
    setTransitioning(true);
    setTimeout(() => { setCurrent(i); setTransitioning(false); }, 300);
  };

  const slide = SLIDES[current];

  return (
    <section className="relative min-h-screen overflow-hidden bg-gray-950">

      {/* ── Background slides ─────────────────────────────────────────────── */}
      {SLIDES.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            i === current && !transitioning ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            className="w-full h-full object-cover scale-105"
            style={{ transform: i === current ? "scale(1)" : "scale(1.05)", transition: "transform 6s ease-out" }}
          />
        </div>
      ))}

      {/* ── Overlays ──────────────────────────────────────────────────────── */}
      <div className="absolute inset-0 bg-black/35" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent" />

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div className="relative z-10 min-h-screen flex flex-col justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-36 pb-36">

          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex gap-1">
              <span className="w-6 h-0.5 bg-[#c4873a]" />
              <span className="w-2 h-0.5 bg-[#c4873a]/50" />
            </div>
            <span className="text-[#c4873a] text-xs font-semibold tracking-[0.25em] uppercase">
              Sherpa Trekker · Nepal
            </span>
          </div>

          {/* Headline — Fraunces font */}
          <h1
            className={`text-white leading-[1.05] mb-6 max-w-4xl transition-all duration-500 ${
              transitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
            style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 300,
              fontSize: "clamp(3rem, 7vw, 6rem)",
            }}
          >
            {slide.title.split("\n").map((line, i) => (
              <span key={i} className="block">
                {i === 1 ? (
                  <span style={{ color: "#c4873a", fontStyle: "italic" }}>{line}</span>
                ) : (
                  line
                )}
              </span>
            ))}
          </h1>

          {/* Subtitle */}
          <p
            className={`text-white/75 text-lg sm:text-xl max-w-lg mb-10 leading-relaxed transition-all duration-500 delay-100 ${
              transitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
            style={{ fontFamily: "'DM Sans', sans-serif" }}
          >
            {slide.sub}
          </p>

          {/* CTA buttons */}
          <div
            className={`flex flex-wrap gap-4 mb-16 transition-all duration-500 delay-150 ${
              transitioning ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"
            }`}
          >
            <button
              onClick={() => navigate("/trek-discovery-hub")}
              className="group flex items-center gap-2.5 px-8 py-4 text-white font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-2xl text-sm"
              style={{ background: "#c4873a", fontFamily: "'DM Sans', sans-serif" }}
            >
              <Icon name="Compass" size={17} />
              Explore Treks
              <Icon name="ArrowRight" size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <button
              onClick={() => navigate("/booking-consultation-center")}
              className="flex items-center gap-2.5 px-8 py-4 bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/25 text-white font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5 text-sm"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              <Icon name="Calendar" size={17} />
              Book Consultation
            </button>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap gap-2 sm:gap-px">
            {STATS.map((s, i) => (
              <div
                key={i}
                className={`flex-1 min-w-[80px] flex flex-col items-center justify-center px-3 sm:px-7 py-3 sm:py-4 backdrop-blur-sm rounded-2xl sm:rounded-none ${
                  i === 0 ? "sm:rounded-l-2xl" : ""
                } ${i === STATS.length - 1 ? "sm:rounded-r-2xl" : ""}`}
                style={{ background: "rgba(255,255,255,0.08)", borderRight: i < STATS.length - 1 ? "1px solid rgba(255,255,255,0.12)" : "none" }}
              >
                <p
                  className="text-white font-bold text-2xl leading-none mb-1"
                  style={{ fontFamily: "'Fraunces', serif" }}
                >
                  {s.value}
                </p>
                <p className="text-white/55 text-[10px] sm:text-xs tracking-wide uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Slide dots ────────────────────────────────────────────────────── */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2.5">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`transition-all duration-300 rounded-full ${
              i === current
                ? "w-8 h-2 bg-[#c4873a]"
                : "w-2 h-2 bg-white/35 hover:bg-white/60"
            }`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* ── Thumbnail strip (desktop) ─────────────────────────────────────── */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 z-20 hidden xl:flex flex-col gap-2.5">
        {SLIDES.map((s, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`relative w-14 h-10 rounded-lg overflow-hidden transition-all duration-300 ${
              i === current
                ? "ring-2 ring-[#c4873a] scale-110 shadow-lg opacity-100"
                : "opacity-40 hover:opacity-70"
            }`}
          >
            <img src={s.image} alt="" className="w-full h-full object-cover" />
          </button>
        ))}
      </div>

      {/* ── Scroll hint ───────────────────────────────────────────────────── */}
      <div className="absolute bottom-10 right-8 z-20 hidden sm:flex flex-col items-center gap-2 text-white/40">
        <span className="text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: "'DM Sans', sans-serif" }}>Scroll</span>
        <div className="w-px h-10 bg-white/20 relative overflow-hidden rounded-full">
          <div className="absolute top-0 w-full h-1/2 bg-white/50 animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
